import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { Box, Divider, makeStyles, Theme, Typography } from '@material-ui/core'
import React, { FC, useEffect, useMemo, useState } from 'react'

import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import colors from '../../listingColors'
import {
    PropertiesType,
    PropertyType,
    SupportedOptionsObject,
} from '../ListingDetailsPage'
import { MANUFACTURER_OPTIONS } from './PremiumPlus/constants'
import {
    getMasterSpecPropertyGroup,
    getNonMasterSpecPropertyGroup,
} from './propertiesUtils'
import { Propertyname } from './Propertyname'
import PropertySection from './PropertySection'

const { propertiesDivider, textColor } = colors

const useStyles = makeStyles((theme: Theme) => ({
    noop: {
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        height: 'inherit',
        width: '100vw',
        color: textColor,
        margin: theme.spacing(2),
    },
    divider: {
        backgroundColor: propertiesDivider,
        marginBottom: theme.spacing(1),
        width: '100%',
    },
    premiumHeader: {
        marginTop: theme.spacing(1),
    },
}))

interface PropertiesContainerProps {
    properties: PropertiesType
    selectedData: SupportedOptionsObject
    mode: string
    listingType?: ListingLevel
}

const PropertiesContainer: FC<PropertiesContainerProps> = ({
    properties,
    selectedData,
    mode,
    listingType,
}) => {
    const classes = useStyles()

    const [masterSpec, setMasterSpec] = useState<Array<[string, PropertyType]>>(
        []
    )

    const [nonMasterSpec, setNonMasterSpec] = useState<
        Array<[string, PropertyType]>
    >([])

    const [nonPremium, setNonPremium] = useState<Array<[string, PropertyType]>>(
        []
    )

    const isPremium = useMemo(
        () =>
            [
                ListingLevel.Premium,
                ListingLevel.PremiumMasterSpec,
                ListingLevel.ProductListing,
            ].includes(listingType as ListingLevel),
        [listingType]
    )

    useEffect(() => {
        if (mode === 'premPlus') {
            let nonPremiumObject: Array<[string, PropertyType]> = []

            Object.entries(properties).forEach(([propertyName, property]) => {
                const properties = property.filter(
                    (prop) => !prop.isPremiumPlus
                )

                if (properties.length) {
                    nonPremiumObject.push([propertyName, properties])
                }
            })

            setNonPremium(nonPremiumObject)
        }

        if (mode === 'edit' && isPremium) {
            setMasterSpec(
                Object.entries(getMasterSpecPropertyGroup(properties))
            )
            setNonMasterSpec(
                Object.entries(getNonMasterSpecPropertyGroup(properties))
            )
        }
    }, [
        properties,
        selectedData,
        mode,
        isPremium,
        getMasterSpecPropertyGroup,
        getNonMasterSpecPropertyGroup,
    ])

    return (
        <>
            {mode === 'premPlus' &&
                (nonPremium.length ? (
                    nonPremium.map(([propertyName, property]) => {
                        return (
                            <PropertySection
                                propertyName={propertyName}
                                property={property}
                                hasDivider
                                mode={mode}
                                selectedData={selectedData}
                                key={propertyName}
                            />
                        )
                    })
                ) : (
                    <Box className={classes.noop}>
                        <Typography component="span" variant="h6">
                            No Masterspec Properties Available
                        </Typography>
                    </Box>
                ))}
            {mode === 'edit' && (
                <>
                    {!!masterSpec.length &&
                        masterSpec.map(([propertyName, property], index) => {
                            return (
                                <PropertySection
                                    propertyName={propertyName}
                                    property={property}
                                    hasDivider={
                                        !!(index !== masterSpec.length - 1)
                                    }
                                    mode={mode}
                                    selectedData={selectedData}
                                    key={propertyName}
                                />
                            )
                        })}
                    {!!nonMasterSpec.length &&
                        nonMasterSpec.some(([_, props]) =>
                            props.some((prop) => prop.isPremiumPlus)
                        ) && (
                            <>
                                <GridItem
                                    xs={12}
                                    className={classes.premiumHeader}
                                >
                                    <GridContainer>
                                        <Propertyname
                                            propertyName={MANUFACTURER_OPTIONS}
                                        />
                                        <Divider
                                            classes={{
                                                root: classes.divider,
                                            }}
                                        />
                                    </GridContainer>
                                </GridItem>
                                {nonMasterSpec
                                    .reduce(
                                        (
                                            acc: [string, PropertyType][],
                                            [propertyName, property]
                                        ) => {
                                            if (propertyName === '') {
                                                const filteredProps = property.filter(
                                                    (subProp) =>
                                                        !!subProp.isPremiumPlus
                                                )
                                                acc.push([
                                                    propertyName,
                                                    filteredProps,
                                                ])
                                            } else {
                                                acc.push([
                                                    propertyName,
                                                    property,
                                                ])
                                            }
                                            return acc
                                        },
                                        []
                                    )
                                    .map(([propertyName, property]) => {
                                        return (
                                            <PropertySection
                                                propertyName={propertyName}
                                                property={property}
                                                hasDivider
                                                mode={mode}
                                                selectedData={selectedData}
                                                key={propertyName}
                                            />
                                        )
                                    })}
                            </>
                        )}
                    {!masterSpec.length && !nonMasterSpec.length && (
                        <Box className={classes.noop}>
                            <Typography component="span" variant="h6">
                                No Properties Available
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </>
    )
}

export default PropertiesContainer
